import { Injectable, signal, computed } from '@angular/core';
import { Program } from '../../models/programs.model';
import { ProgramsService } from '../api/programs.api.service';
import { finalize, Observable, of, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ProgramsStore {
  private readonly _programs = signal<Program[]>([]);
  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly programs = computed(() => this._programs());
  readonly isLoading = computed(() => this._isLoading());
  readonly error = computed(() => this._error());
  readonly hasData = computed(() => this._programs().length > 0);

  constructor(private programsService: ProgramsService) {}

  load(options: { force?: boolean } = {}): Observable<Program[]> {
    const { force = false } = options;

    // If already loaded and not forcing → just return current value as Observable
    if (!force && this._programs().length > 0) {
      return of(this._programs());
    }

    this._isLoading.set(true);
    this._error.set(null);

    return this.programsService.listAll().pipe(
      tap({
        next: (programs) => {
          this._programs.set(programs ?? []);
        },
        error: (err) => {
          this._error.set('Error loading programs');
          this._programs.set([]);
        },
      }),
      finalize(() => {
        this._isLoading.set(false);
      })
    );
  }

  refresh(): Observable<Program[]> {
    return this.load({ force: true });
  }

  /** Get Program by ID*/
  getById(id: string): Program | undefined {
    return this._programs().find((e) => e.id === id);
  }

  /** Get only the displayLabel/name by id */
  getNameById(id: string): string | null {
    const program = this.getById(id);
    return program ? program.displayLabel : null;
  }
}
